import { Grid, TableCell } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { Table } from "../../../../layout";
import { Button, InputCheck } from "../../../../controls";
import {
  CloseRounded,
  DoneAllRounded,
  EditNoteRounded,
  LocalShippingRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Printer } from "lucide-react";
import { lsSave, useFormatPrice } from "hooks";
import { AppContext } from "App";

const Products = ({ order, setShowCancelForm, handleMarkAsSent }) => {
  const formatPrice = useFormatPrice();
  const { access } = useContext(AppContext);
  const navigate = useNavigate();
  const [selectedRec, setSelectedRec] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [invOutlets, setInvOutlets] = useState([]);
  const [accounts, setAccounts] = useState({
    totalOrderedQty: 0,
    totalPurchPrice: 0,
    totalRetailPrice: 0,
  });

  const [tblData, setTblData] = useState([]);

  const handleRecSelect = (e, item) => {
    if (e.target.checked) {
      setSelectedRec((prev) => [
        ...prev,
        {
          key: item.id,
          nol: item.totalReceiveQty,
          splrPrice: item.splrPrice,
          price: item.rtlPrice,
          prodTitle: item.label,
          prodType: item.isVariant ? 2 : 1,
          prodId: item.prodId,
          varProdId: item.varProdId,
          prod: item.prod,
          varProd: item.varProd,
        },
      ]);
    } else {
      setSelectedRec((prev) => prev.filter((j) => j.key !== item.id));
    }
  };

  useEffect(() => {
    if (selectAll && selectedRec.length !== tblData.length) setSelectAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRec.length]);

  const handleSelectAll = (e) => {
    if (e.target.checked)
      setSelectedRec(
        tblData.map((item) => ({
          key: item.id,
          nol: item.totalReceiveQty,
          splrPrice: item.splrPrice,
          price: item.rtlPrice,
          prodTitle: item.label,
          prodType: item.isVariant ? 2 : 1,
          prod: item.prod,
          varProd: item.varProd,
        }))
      );
    else setSelectedRec([]);
  };

  useEffect(() => {
    if (order && order.stockProds) {
      // table data
      setTblData([
        ...order.stockProds.map((prod, ind) => {
          const flattenedProd = {
            ...prod,
            order: ind + 1,
            name: (
              <div className="flex flex-col min-w-[350px] !max-w-[600px] whitespace-normal">
                <span className="text-[15px] font-medium">{prod.label}</span>
                {(prod.mfgDate || prod.expDate) && (
                  <span className="text-[12px] font-medium">
                    MFG: {moment(prod.mfgDate).format("MM/DD/YYYY")} | EXP:{" "}
                    {moment(prod.expDate).format("MM/DD/YYYY")}
                  </span>
                )}
              </div>
            ),
            skuAndSplrCode: (
              <div className="flex">
                <div className="grid text-[14px]">
                  <span>SKU &nbsp;&nbsp;</span>
                  <span>Supplier Code &nbsp;&nbsp;</span>
                </div>
                <div className="grid text-[14px]">
                  <span className="font-medium">: {prod.sku}</span>
                  <span className="font-medium">: {prod.splrCode}</span>
                </div>
              </div>
            ),
            stock: (
              <div className="grid text-[14px] w-max">
                <div className="flex gap-3 border-b-[1px]">
                  <div>
                    <span>Ordered:&nbsp;</span>
                    <span className="font-medium">{prod.totalOrderQty}</span>
                  </div>
                  <div>
                    <span>Received:&nbsp;</span>
                    <span className="font-medium">{prod.totalReceiveQty}</span>
                  </div>
                </div>
                <div>
                  <span>Previous Stock:&nbsp;</span>
                  <span className="font-medium">{prod.totalPrevStock}</span>
                </div>
              </div>
            ),
            purchPrice: (
              <div className="flex">
                <div className="grid text-[14px]">
                  <span>Cost Price &nbsp;&nbsp;</span>
                  <span>Total Cost Price &nbsp;&nbsp;</span>
                </div>
                <div className="grid text-[14px]">
                  <span className="font-medium">
                    : {formatPrice(prod.unitPrice)}
                  </span>
                  <span className="font-medium">
                    : {formatPrice(prod.totalPrice)}
                  </span>
                </div>
              </div>
            ),
            retailPrice: (
              <div className="flex">
                <div className="grid text-[14px]">
                  <span>Retail Price &nbsp;&nbsp;</span>
                  <span>Total Retail Price &nbsp;&nbsp;</span>
                </div>
                <div className="grid text-[14px]">
                  <span className="font-medium">
                    : {formatPrice(prod.retailPrice)}
                  </span>
                  <span className="font-medium">
                    : {formatPrice(prod.totalRetailPrice)}
                  </span>
                </div>
              </div>
            ),
          };

          if (prod.orderInvsFor) {
            prod.orderInvsFor.forEach((inv) => {
              flattenedProd[inv.outletId] = (
                <div className="grid text-[14px] w-max">
                  <div className="flex gap-3 border-b-[1px]">
                    <div>
                      <span>Ordered:&nbsp;</span>
                      <span className="font-medium">{inv.qty}</span>
                    </div>
                    <div>
                      <span>Received:&nbsp;</span>
                      <span className="font-medium">{inv.receiveQty}</span>
                    </div>
                  </div>
                  <div>
                    <span>Previous Stock:&nbsp;</span>
                    <span className="font-medium">{inv.previousStock}</span>
                  </div>
                </div>
              );
            });
          }

          return flattenedProd;
        }),
      ]);

      // Set Accounting Properties.
      var _stockProds = order.stockProds;
      var _totaOrderQty = _stockProds
        .map((j) => j.totalOrderQty)
        .reduce((x, y) => x + y, 0)
        .toLocaleString("en-US", {
          maximumFractionDigits: 3,
        });

      var _totalReceiveQty = _stockProds
        .map((j) => j.totalReceiveQty)
        .reduce((x, y) => x + y, 0)
        .toLocaleString("en-US", {
          maximumFractionDigits: 3,
        });

      var _totalCostPrice = _stockProds
        .map((j) => j.totalPrice)
        .reduce((x, y) => x + y, 0);

      var _totalRetailPrice = _stockProds
        .map((j) => j.totalRetailPrice)
        .reduce((x, y) => x + y, 0);

      setAccounts({
        totalOrderQty: _totaOrderQty,
        totalReceiveQty: _totalReceiveQty,
        totalCostPrice: _totalCostPrice,
        totalRetailPrice: _totalRetailPrice,
      });

      // Inv Outlets
      _stockProds[0] && setInvOutlets([..._stockProds[0].orderInvsFor]);
    } else
      setAccounts({
        totalOrderedQty: 0,
        totalPreviousStock: 0,
        totalPurchPrice: 0,
        totalRetailPrice: 0,
      });
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const handleCancel = () => {
    setShowCancelForm(true);
  };

  const handleReceived = () => {
    navigate(`/receive_delivery_form/${order.id}`);
  };

  const handleEdit = () => {
    navigate(`/order_stock_form/${order.id || ""}`);
  };

  // Redirect to Print Labels Page
  const handlePrint = () => {
    lsSave("printPurchaseLabels", {
      orderId: order.id,
      items: selectedRec,
    });
    navigate("/print_new_label");
  };

  const HeaderCom = () => (
    <div className="flex gap-[10px] flex-wrap">
      {selectedRec.length > 0 && (
        <Button
          startIcon={<Printer height={20} />}
          onClick={() => handlePrint()}
          variant="text"
        >
          Print Labels
        </Button>
      )}
      {order.status !== 3 && order.status !== 2 && (
        <>
          <Button
            startIcon={<DoneAllRounded />}
            onClick={() => handleReceived()}
          >
            Received
          </Button>
          {order.status !== 4 && (
            <Button
              startIcon={<LocalShippingRounded />}
              color="primary"
              onClick={() => handleMarkAsSent()}
            >
              Mark As Sent
            </Button>
          )}
          <Button
            onClick={handleEdit}
            startIcon={<EditNoteRounded />}
            color="primary"
          >
            Edit Order
          </Button>
          <Button
            startIcon={<CloseRounded />}
            color="error"
            onClick={() => handleCancel()}
          >
            Cancel Order
          </Button>
        </>
      )}
    </div>
  );

  return (
    <>
      <Grid item xs={12}>
        {order.stockProds ? (
          <Table
            ActionComp={() => <></>}
            HeaderComp={HeaderCom}
            tblHead={[
              {
                id: "select",
                label: access?.stock?.labelPrint && (
                  <InputCheck
                    checked={selectAll}
                    onChange={(e) => setSelectAll(e.target.checked)}
                    onClick={handleSelectAll}
                  />
                ),
              },
              { id: "order", label: "#" },
              { id: "label", label: "Product" },
              {
                id: "skuAndSplrCode",
                label: " Supplier Code / SKU",
              },
              { id: "stock", label: "Quantity" },
              { id: "purchPrice", label: "Cost Price" },
              { id: "retailPrice", label: "Retail Price" },
              ...invOutlets.map((j, ind) => ({
                id: j.outletId,
                label: (
                  <span className="block w-max">{`${
                    j.outlet && j.outlet.outletName
                  }`}</span>
                ),
              })),
            ]}
            tblData={tblData.map((item) => ({
              ...item,
              select: (
                <InputCheck
                  checked={selectedRec.some((s) => s.key === item.id)}
                  name={`${item.id}${item.label}`}
                  onChange={(e) => handleRecSelect(e, item)}
                />
              ),
            }))}
            FooterRow={() => (
              <>
                <TableCell className="!text-[16px] !font-medium uppercase">
                  Total
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>

                <TableCell className="w-max !text-[16px] !font-medium !text-slate-500 !max-w-max">
                  <div className="flex w-max">
                    <div className="grid text-[14px] w-max">
                      <span>Receive &nbsp;&nbsp;</span>
                      <span>Ordered &nbsp;&nbsp;</span>
                    </div>
                    <div className="grid text-[14px]">
                      <span className="font-medium w-max">
                        : {accounts.totalReceiveQty}
                      </span>
                      <span className="font-medium w-max">
                        : {accounts.totalOrderQty}
                      </span>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="!text-[20px] !text-slate-500 !font-medium">
                  {formatPrice(accounts.totalCostPrice)}
                </TableCell>
                <TableCell className="!text-[20px] !text-slate-500 !font-medium">
                  {formatPrice(accounts.totalRetailPrice)}
                </TableCell>
              </>
            )}
          />
        ) : (
          <span className="text-[20px] font-medium">No Data</span>
        )}
      </Grid>
    </>
  );
};

export default Products;
